import React, { ReactElement, useEffect, useState } from "react";
import styles from "./planDetailV2.module.scss";
import { Divider } from "@kunduz/ui";
import { Button, Text } from "@kunduz/ui-atoms";
import {
  PackageDescription,
  PackageTitle,
  PaymentOptions,
  PlanDetailBannerCta,
  PlanDetailSummary,
  PlanDetailV2Breadcrumb,
  gray,
  PlanDetailPlainCarousel,
} from "@kunduz/ui-components";
import { type IContent } from "@kunduz/data-access-cms-api";
import { getCmsIcon } from "../static/iconMappings";
import { useTranslation } from "next-i18next";
import { PlanDetailAccordion } from "./planDetailAccordion";
import classNames from "classnames";
import { ArrowRight } from "@untitled-ui/icons-react";
import { PlanDetailReviews } from "./planDetailReviews";
import { ProductJsonLd } from "next-seo";
import {
  CheckoutEvents,
  sendViewContentDatalayerEvent,
  trackEventNext,
} from "@kunduz/utility-analytics";
import { AccordionTypes } from "./accordionTypesEnum";
import { TableWithContext } from "../table";

export const PlanDetailV2 = (props: Props): ReactElement => {
  const { t } = useTranslation("kunduzcom_common");

  const [selectedPlan, setSelectedPlan] = useState<
    IContent.PlanDetailV2PaymentOptionData["items"][0] | undefined
  >(
    props.data.options.find((x) => x.type === "packagedetailpaymentoption")
      ?.data?.items[0],
  );

  const metaData:
    | IContent.PlanDetailV2Option<IContent.PlanDetailV2MetaData>
    | undefined = props.data.options.find(
    (x) => x.type === "packagedetailmetaoption",
  );
  const carouselData:
    | IContent.PlanDetailV2Option<IContent.PlanDetailV2CarouselData>
    | undefined = props.data.options.find(
    (x) => x.type === "carouseloptioncomponent",
  );
  const paymentData:
    | IContent.PlanDetailV2Option<IContent.PlanDetailV2PaymentOptionData>
    | undefined = props.data.options.find(
    (x) => x.type === "packagedetailpaymentoption",
  );
  const reviewData:
    | IContent.PlanDetailV2Option<IContent.PlanDetailV2ReviewData>
    | undefined = props.data.options.find(
    (x) => x.type === "reviewsummarycomponent",
  );

  useEffect(() => {
    if (!paymentData || !paymentData.data.items[0]) return;

    const items = paymentData.data.items;
    const defaultItem = items[0];
    const currency = defaultItem.total_price.includes("₺") ? "TRY" : "USD";

    CheckoutEvents.plansListViewed({
      currency,
      items: items.map((p, i) => ({
        item_id: p.package_id + "",
        item_name: p.package_name,
        affiliation: "Kunduz",
        index: i,
        quantity: 1,
        price: parseFloat(
          p.total_price?.replace("₺", "").replace(".", "").replace(",", "."),
        ),
      })),
    });

    const kunduz_event_id = CheckoutEvents.planDetailViewed({
      currency,
      value: parseFloat(
        defaultItem.total_price
          .replace("₺", "")
          .replace(".", "")
          .replace(",", "."),
      ),
      items: [
        {
          item_id: defaultItem.package_id,
          item_name: defaultItem.package_name,
          affiliation: "Kunduz",
          index: 0,
          quantity: 1,
          price: parseFloat(
            defaultItem.total_price
              .replace("₺", "")
              .replace(".", "")
              .replace(",", "."),
          ),
        },
      ],
    });

    sendViewContentDatalayerEvent({
      content_type: "product",
      content_ids: items.map((item) => item.package_id),
      kunduz_event_id,
    } as any);

    trackEventNext({
      event: "Package Detail Page Visited",
      kunduz_event_id,
    });
  }, [paymentData]);

  const handlePurchaseClicked = (label?: string) => {
    if (!selectedPlan) return;

    const currency = selectedPlan.total_price.includes("₺") ? "TRY" : "USD";

    const kunduz_event_id = CheckoutEvents.planAddedToCard({
      currency,
      value: parseFloat(
        selectedPlan.total_price
          .replace("₺", "")
          .replace(".", "")
          .replace(",", "."),
      ),
      items: [
        {
          item_id: selectedPlan.package_id.toString(),
          item_name: selectedPlan.package_name,
          affiliation: "Kunduz",
          index: 0,
          quantity: 1,
          price: parseFloat(
            selectedPlan.total_price
              .replace("₺", "")
              .replace(".", "")
              .replace(",", "."),
          ),
        },
      ],
    });

    trackEventNext({
      event: "Package Purchase Button Clicked",
      label: label,
      redirect_url: selectedPlan?.checkout_link,
      kunduz_event_id,
    });
  };

  // const prerenderUrls: string[] =
  //   paymentData?.data.items
  //     .map((x) => {
  //       return [
  //         x.checkout_link,
  //         x.checkout_link.replace("checkout", "guestCheckout"),
  //       ];
  //     })
  //     .reduce((prev = [], curr, index) => {
  //       return [...prev, ...curr];
  //     }) || [];

  const onChangeSelectedPlan = (item) => {
    setSelectedPlan(item);

    const currency = item.total_price.includes("₺") ? "TRY" : "USD";

    CheckoutEvents.planDetailViewed({
      currency,
      value: parseFloat(
        item.total_price.replace("₺", "").replace(".", "").replace(",", "."),
      ),
      items: [
        {
          item_id: item.package_id,
          item_name: item.package_name,
          affiliation: "Kunduz",
          index: 0,
          quantity: 1,
          price: parseFloat(
            item.total_price
              .replace("₺", "")
              .replace(".", "")
              .replace(",", "."),
          ),
        },
      ],
    });
  };

  const PaymentOptionsComponent = ({
    data,
    idPrefix,
  }: {
    data?: IContent.PlanDetailV2Option<IContent.PlanDetailV2PaymentOptionData>;
    idPrefix: string;
  }) => {
    return data?.data?.items?.map((item, i) => (
      <PaymentOptions.Wrapper
        key={`po_d_${i}`}
        id={`id_${idPrefix}_${i}`}
        name={`planselect_${idPrefix}`}
        value={item.checkout_link}
        onChange={() => onChangeSelectedPlan(item)}
        checked={item === selectedPlan}
      >
        <PaymentOptions.Indicator />
        <PaymentOptions.Content>
          <PaymentOptions.SpaceBetweenRow>
            <Text
              variant="textsm"
              weight="semibold"
              style={{ color: gray[900] }}
            >
              {item.name}{" "}
              {item.recurring_count_text && (
                <span style={{ fontWeight: 400 }}>
                  ({item.recurring_count_text})
                </span>
              )}
            </Text>
            <Text
              variant="textsm"
              weight="semibold"
              style={{ color: gray[900] }}
            >
              {item.monthly_price}
              {item.period}
            </Text>
          </PaymentOptions.SpaceBetweenRow>
          <PaymentOptions.SpaceBetweenRow>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {item.badges?.map((b) => (
                <PaymentOptions.Badge
                  key={`b_${b.bg_color}_${b.order}`}
                  bgColor={b.bg_color}
                >
                  <Text weight="bold">{b.name}</Text>
                </PaymentOptions.Badge>
              ))}
            </div>

            <Text
              variant="textxs"
              weight="semibold"
              style={{
                textDecoration: "line-through",
                color: gray[400],
              }}
            >
              {item.monthly_base_price}
              {item.period}
            </Text>
          </PaymentOptions.SpaceBetweenRow>

          <Text
            variant="textxs"
            style={{ color: gray[600] }}
            dangerouslySetInnerHTML={{ __html: item.description || "" }}
            as="div"
          ></Text>
        </PaymentOptions.Content>
      </PaymentOptions.Wrapper>
    ));
  };

  const handleCarouselItemClicked = (id: any, type: any) => {
    trackEventNext({
      event: "Media Carousel Item Opened",
      id,
      type,
    });
  };

  return (
    <>
      <ProductJsonLd
        productName={metaData?.data.title || ""}
        description={metaData?.data.description || ""}
        images={
          carouselData?.data.items
            ?.filter((x) => x.is_video == false)
            .map((x) => x.file || "") || []
        }
        brand="Kunduz"
        aggregateRating={{
          ratingValue: metaData?.data.rating,
          reviewCount: reviewData?.data.items.count,
        }}
        reviews={reviewData?.data.items.results.map((x) => ({
          author: x.user,
          reviewBody: x.message,
          name: x.message_title,
          reviewRating: {
            bestRating: "5",
            ratingValue: x.rating,
            worstRating: "1",
          },
        }))}
      />

      {/* <SpeculationRules prerender={prerenderUrls} /> */}

      <div className={styles.base}>
        <div className={styles.wrapper}>
          <main className={styles.main}>
            <PlanDetailV2Breadcrumb title={metaData?.data.title || ""} />

            {props.data.options?.map((c, i) => {
              switch (c.type) {
                case "packagedetailmetaoption":
                  return (
                    <section
                      key={`plan_detail_comp_${i}`}
                      className={classNames(
                        styles.metaWrapper,
                        styles.sectionWithBorderBottom,
                      )}
                      style={{ order: (i + 1) * 10 }}
                    >
                      <PackageTitle
                        badgeText={c.data.badge_text}
                        rating={c.data.rating}
                        reviewLabel={c.data.review}
                        size="responsive"
                        title={c.data.title}
                        onReviewsClicked={() => {
                          trackEventNext({
                            event: "Rating Component Clicked",
                            component: "meta",
                          });
                          window.scrollTo({
                            behavior: "smooth",
                            top:
                              (document.getElementById("reviews")?.offsetTop ||
                                40) - 40,
                          });
                        }}
                      />
                      <Divider top={0} bottom={0} />
                      {c.data.description && (
                        <PackageDescription>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: c.data.description,
                            }}
                          ></div>
                        </PackageDescription>
                      )}
                    </section>
                  );

                case "carouseloptioncomponent":
                  return (
                    <section
                      key={`plan_detail_comp_${i}`}
                      className={classNames(
                        styles["mobileCarousel"],
                        styles.sectionWithBorderBottom,
                      )}
                    >
                      <PlanDetailPlainCarousel
                        items={c.data.items.map((x) => {
                          return {
                            kind: x.is_video ? "video" : "image",
                            media: x.file as string,
                            thumbnail: x.thumbnail as string,
                            wistiaId: x.wistia_video_id,
                            id: x.id,
                          };
                        })}
                        width={310}
                        height={175}
                        onClick={handleCarouselItemClicked}
                      />
                    </section>
                  );

                case "packagedetailsummaryoption":
                  return (
                    <section
                      key={`plan_detail_comp_${i}`}
                      className={styles.sectionWithBorderBottom}
                      style={{ order: (i + 1) * 10 }}
                    >
                      <PlanDetailSummary.ItemList>
                        <PlanDetailSummary.Title>
                          {c.data.feature_group?.name}
                        </PlanDetailSummary.Title>
                        {c.data.feature_group?.features.map((f) => (
                          <PlanDetailSummary.Item
                            key={`fg_${f.order}`}
                            inactive={f.is_strikethrough}
                          >
                            {f.text}
                          </PlanDetailSummary.Item>
                        ))}
                      </PlanDetailSummary.ItemList>
                      <PlanDetailSummary.ItemList>
                        <PlanDetailSummary.Title>
                          {c.data.content_group?.name}
                        </PlanDetailSummary.Title>
                        {c.data.content_group?.contents.map((f) => (
                          <PlanDetailSummary.Item
                            key={`cg_${f.order}`}
                            inactive={f.is_strikethrough}
                          >
                            {f.text}
                          </PlanDetailSummary.Item>
                        ))}

                        <PlanDetailSummary.Title>
                          {c.data.benefit_group?.name}
                        </PlanDetailSummary.Title>
                        {c.data.benefit_group?.benefits.map((f) => (
                          <PlanDetailSummary.Item
                            key={`bg_${f.order}`}
                            inactive={f.is_strikethrough}
                          >
                            {f.text}
                          </PlanDetailSummary.Item>
                        ))}
                      </PlanDetailSummary.ItemList>
                    </section>
                  );

                case "packagedetailctabanneroption":
                  return (
                    <section
                      key={`plan_detail_comp_${i}`}
                      className={styles.sectionWithBorderBottom}
                      style={{ order: (i + 1) * 10 }}
                    >
                      <PlanDetailBannerCta
                        bgColor={c.data.bg_color}
                        title={c.data.title}
                        description={c.data.description}
                        buttonUrl={c.data.cta_link}
                        buttonLabel={c.data.cta_link_label}
                        imageUrl={c.data.image}
                        buttonIcon={getCmsIcon(c.data.cta_link_icon)}
                        buttonTarget={c.data.cta_link_target}
                      />
                    </section>
                  );

                case "packagedetailpaymentoption":
                  return (
                    <React.Fragment key={`plan_detail_comp_${i}`}>
                      <section
                        className={classNames(
                          styles.sectionWithBorderBottom,
                          styles["mobilePaymentOptions"],
                        )}
                        style={{ order: (i + 1) * 10 }}
                      >
                        <PaymentOptionsComponent data={c} idPrefix="mobile" />
                      </section>

                      <section className={styles["mobilePaymentCta"]}>
                        <div className={styles.priceLine}>
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            <Text
                              variant="textmd"
                              weight="semibold"
                              color={gray[700]}
                            >
                              {t("package_detail_total_label")}
                            </Text>
                            {selectedPlan?.recurring_count_text ? (
                              <Text
                                variant="textsm"
                                weight="medium"
                                color={gray[600]}
                              >
                                ({selectedPlan.recurring_count_text} x{" "}
                                {selectedPlan.monthly_price})
                              </Text>
                            ) : (
                              <Text
                                variant="textsm"
                                weight="medium"
                                color={gray[600]}
                              >
                                ({t("package_detail_mothly_label")})
                              </Text>
                            )}
                          </div>
                          <Text
                            variant="textmd"
                            weight="semibold"
                            color={gray[700]}
                          >
                            {selectedPlan?.total_price}
                          </Text>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          <Button
                            color="blue"
                            variant="primary"
                            size="xl"
                            as={"a"}
                            href={selectedPlan?.checkout_link || "#"}
                            onClick={() => {
                              handlePurchaseClicked(
                                paymentData?.data.primary_button_label,
                              );
                            }}
                            style={{
                              width: "100%",
                            }}
                          >
                            <Text variant="textmd" weight="semibold">
                              {paymentData?.data.primary_button_label}{" "}
                              <ArrowRight />
                            </Text>
                          </Button>

                          <Text
                            variant="textxs"
                            style={{ textAlign: "center", color: gray[600] }}
                          >
                            {t("plan_detail_page_disclaimer")}
                          </Text>
                        </div>
                      </section>
                    </React.Fragment>
                  );

                case AccordionTypes.AccordionOption:
                  return (
                    <section
                      key={`plan_detail_comp_${i}`}
                      className={styles["accordionSection"]}
                      style={{ order: (i + 1) * 10 }}
                    >
                      <PlanDetailAccordion accordionData={c} />
                    </section>
                  );

                case "reviewsummarycomponent":
                  return (
                    <PlanDetailReviews
                      key={`plan_detail_comp_${i}`}
                      style={{ order: (i + 1) * 10 }}
                      metaData={metaData}
                      componentData={c}
                    />
                  );
                case "tablecomponent":
                  return (
                    <TableWithContext
                      key={`plan_detail_comp_${i}`}
                      data={c.data as any}
                      style={{ order: (i + 1) * 10 }}
                    />
                  );
                default:
                  break;
              }
            })}
          </main>

          <aside className={styles.aside}>
            <div className={styles.sticky}>
              <div className={styles.plansWrapper}>
                {paymentData?.data.badge_text && (
                  <PaymentOptions.DiagonalBadge
                    bgColor={paymentData.data.badge_bg_color || "#F670C7"}
                  >
                    {paymentData.data.badge_text}
                  </PaymentOptions.DiagonalBadge>
                )}
                {metaData && (
                  <PackageTitle
                    badgeText={metaData.data.badge_text}
                    rating={metaData.data.rating}
                    reviewLabel={metaData.data.review}
                    size="xs"
                    title={metaData.data.title}
                    onReviewsClicked={() => {
                      trackEventNext({
                        event: "Rating Component Clicked",
                        component: "payment_options",
                      });
                      window.scrollTo({
                        behavior: "smooth",
                        top:
                          (document.getElementById("reviews")?.offsetTop ||
                            40) - 40,
                      });
                    }}
                  />
                )}

                <Divider top={0} bottom={0} color={gray[200]} />

                <div>
                  <PaymentOptionsComponent
                    data={paymentData as any}
                    idPrefix="desktop"
                  />
                </div>

                <Divider top={0} bottom={0} color={gray[200]} />

                <div className={styles.priceLine}>
                  <div
                    style={{ display: "flex", gap: 4, alignItems: "center" }}
                  >
                    <Text variant="textmd" weight="semibold" color={gray[700]}>
                      {t("package_detail_total_label")}
                    </Text>
                    {selectedPlan?.recurring_count_text ? (
                      <Text variant="textsm" weight="medium" color={gray[600]}>
                        ({selectedPlan.recurring_count_text} x{" "}
                        {selectedPlan.monthly_price})
                      </Text>
                    ) : (
                      <Text variant="textsm" weight="medium" color={gray[600]}>
                        ({t("package_detail_mothly_label")})
                      </Text>
                    )}
                  </div>
                  <Text variant="textmd" weight="semibold" color={gray[700]}>
                    {selectedPlan?.total_price}
                  </Text>
                </div>

                <Button
                  color="blue"
                  variant="primary"
                  size="xl"
                  as={"a"}
                  href={selectedPlan?.checkout_link || "#"}
                  onClick={() => {
                    handlePurchaseClicked(
                      paymentData?.data.primary_button_label,
                    );
                  }}
                >
                  <Text variant="textmd" weight="semibold">
                    {paymentData?.data.primary_button_label}
                  </Text>
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

interface Props {
  data: IContent.PlanDetailV2ComponentData;
}
